/**
 * Camera access. The only part of the app that touches getUserMedia.
 */

export interface CameraOptions {
  /** 'environment' is the rear camera, which is what you want for swatches. */
  facingMode?: 'environment' | 'user';
  /** Requested capture size. The browser may return something else. */
  idealWidth?: number;
  idealHeight?: number;
  deviceId?: string;
}

export class CameraError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, { cause });
    this.name = 'CameraError';
  }
}

/** True when the browser exposes a camera API at all. */
export function isCameraSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/**
 * Open a camera stream.
 *
 * Colour-affecting automatic adjustments are left alone here. Locking white
 * balance and exposure would make readings more stable, but support is patchy
 * and inconsistent across browsers — the calibration card is the mechanism that
 * is meant to absorb this variation, so relying on it is the honest design.
 */
export async function startCamera(options: CameraOptions = {}): Promise<MediaStream> {
  if (!isCameraSupported()) {
    throw new CameraError(
      'This browser does not expose a camera. Note that getUserMedia requires a secure context: ' +
        'use https, or localhost during development.',
    );
  }

  const { facingMode = 'environment', idealWidth = 1920, idealHeight = 1080, deviceId } = options;

  const video: MediaTrackConstraints = {
    width: { ideal: idealWidth },
    height: { ideal: idealHeight },
    ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode }),
  };

  try {
    return await navigator.mediaDevices.getUserMedia({ video, audio: false });
  } catch (error) {
    throw new CameraError(describeCameraFailure(error), error);
  }
}

export function stopCamera(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

/**
 * List available video inputs.
 *
 * Labels are empty until permission has been granted at least once, so call
 * this after {@link startCamera} if you intend to show them to the user.
 */
export async function listCameras(): Promise<MediaDeviceInfo[]> {
  if (!isCameraSupported()) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'videoinput');
}

function describeCameraFailure(error: unknown): string {
  const name = error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
      return 'Camera permission was denied. Grant access in your browser settings and try again.';
    case 'NotFoundError':
      return 'No camera was found on this device.';
    case 'NotReadableError':
      return 'The camera is already in use by another application.';
    case 'OverconstrainedError':
      return 'No camera matches the requested settings.';
    default:
      return `Could not start the camera: ${error instanceof Error ? error.message : String(error)}`;
  }
}
